/**
 * PretreatPage - Main pretreat station interface
 *
 * Follows Frontend UI Standards (REFERENCE: CatcherPage.tsx):
 * - Container with animate-enter
 * - Page header with icon box pattern
 * - MetricCards with proper variants
 * - Large touch targets for warehouse gloves
 * - ARIA live regions for accessibility
 *
 * SIMPLIFIED from CatcherPage:
 * - Scan-only (no Accept/Redo buttons)
 * - Auto-processes on scan
 */
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from '@/contexts/LocationContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MetricCard } from '@/features/production-orders/components/metrics/MetricCard';
import { QRScannerInput } from '@/features/catcher-station/components/QRScannerInput';
import { PretreatFeedback } from './PretreatFeedback';
import { usePretreatMutation } from '../hooks/usePretreatMutation';
import { usePretreatStats } from '../hooks/usePretreatStats';
import { useScanFeedback } from '@/hooks/useScanFeedback';
import { useScannerAutoFocus } from '@/hooks/useScannerAutoFocus';
import { ScanErrorModal } from '@/components/ui/scan-error-modal';
import { SupersededStickerDialog } from '@/components/production/SupersededStickerDialog';
import { parseRedoError, parseSupersededError, getUserMessage } from '@/lib/trpc-error-utils';
import { useBackendHeartbeat } from '@/hooks/useBackendHeartbeat';
import { ConnectionStatusBanner } from '@/components/ui/connection-status-banner';
import { Package, Clock, Droplet } from 'lucide-react';

type PretreatScanStatus =
  | 'success'
  | 'already_scanned'
  | 'no_pretreat_needed'
  | 'not_pod_item'
  | 'order_cancelled'
  | 'order_on_hold';

interface GarmentInfo {
  id: string;
  sku: string;
  productName: string;
  size: string | null;
  color: string | null;
  orderId: string;
  orderName: string;
  itemIndex: number;
  totalItems: number;
  status: string;
  requiresPretreat: boolean;
  productionMethod: string;
}

interface PretreatScanResult {
  status: PretreatScanStatus;
  garment?: GarmentInfo;
  holdReason?: string | null;
}

export function PretreatPage() {
  const { t } = useTranslation('production');
  const { selectedLocation } = useLocation();
  const heartbeat = useBackendHeartbeat();
  const [result, setResult] = useState<PretreatScanResult | null>(null);
  const [sessionCount, setSessionCount] = useState(0);

  // Scan feedback (modal-based errors)
  const feedback = useScanFeedback();

  // USB scanners type characters as keyboard input - redirect to scanner field
  useScannerAutoFocus({ paused: feedback.isModalOpen });

  // Stats query
  const { data: stats } = usePretreatStats();

  // Clear result callback for mutation success
  const handleScanComplete = useCallback(() => {
    // Session count incremented in handleScan on success
  }, []);

  // Scan mutation
  const { scanIntoPretreat, isScanning } = usePretreatMutation(handleScanComplete);

  // Handle QR scan
  const handleScan = useCallback(async (qrCode: string) => {
    if (!selectedLocation?.id) return;

    // Clear previous result
    setResult(null);

    try {
      const scanResult = await scanIntoPretreat.mutateAsync({
        qrCode,
        locationId: selectedLocation.id,
      });

      setResult(scanResult);

      // Handle different status responses with appropriate feedback
      // NOTE: 'success' status - no modal needed, PretreatFeedback shows green UI
      // QRScannerInput already played success beep on capture
      if (scanResult.status === 'success') {
        setSessionCount((prev) => prev + 1);
      } else if (scanResult.status === 'already_scanned') {
        feedback.triggerAlreadyScanned('garment');
      } else if (scanResult.status === 'no_pretreat_needed') {
        feedback.triggerWarning(t('pretreat.noPretreatNeeded'), t('pretreat.noPretreatNeededDesc'));
      } else if (scanResult.status === 'not_pod_item') {
        feedback.triggerWarning(t('pretreat.invalidItem'), t('pretreat.invalidItemDesc'));
      } else if (scanResult.status === 'order_cancelled') {
        feedback.triggerOrderCancelled();
      } else if (scanResult.status === 'order_on_hold') {
        feedback.triggerOrderOnHold(scanResult.holdReason);
      }
    } catch (error) {
      const parsed = parseRedoError(error);
      const superseded = parseSupersededError(error);
      if (superseded.isSuperseded) {
        feedback.triggerSuperseded(superseded.replacement);
      } else if (parsed.isRedoError) {
        feedback.triggerRedoBlocked(parsed.replacement);
      } else if ('isOrderItemRemoved' in parsed && parsed.isOrderItemRemoved) {
        feedback.triggerOrderItemRemoved();
      } else {
        feedback.triggerError(t('scanError'), getUserMessage(error));
      }
    }
  }, [selectedLocation?.id, scanIntoPretreat, feedback, t]);

  // Clear scan result when location changes
  useEffect(() => {
    setResult(null);
  }, [selectedLocation?.id]);

  // Handle result dismiss
  const handleDismiss = useCallback(() => {
    setResult(null);
  }, []);

  // Auto-clear result after 3 seconds
  useEffect(() => {
    if (result) {
      const timer = setTimeout(() => {
        setResult(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [result]);

  // Location guard
  if (!selectedLocation) {
    return (
      <div className="container mx-auto px-6 py-6 max-w-7xl animate-enter">
        <Card>
          <CardContent className="py-16 text-center">
            <Droplet className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">
              {t('pretreat.selectLocation')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-6 max-w-7xl space-y-6 animate-enter">
      {/* ARIA live region for screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {isScanning && t('lookingUp')}
        {result?.status === 'success' && `Pretreat scan successful: ${result.garment?.productName}`}
        {result?.status === 'already_scanned' && 'Warning: Garment already scanned into pretreat'}
        {result?.status === 'no_pretreat_needed' && 'This garment does not require pretreat'}
        {result?.status === 'not_pod_item' && 'Error: Only POD items need pretreat'}
        {result?.status === 'order_cancelled' && 'Error: Order has been cancelled'}
        {result?.status === 'order_on_hold' && 'Warning: Order is on hold'}
      </div>

      {/* Page header with icon box pattern (MATCH CatcherPage lines 124-133) */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <Droplet className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">{t('pretreat.title')}</h1>
          <p className="text-muted-foreground">{t('pretreat.description')}</p>
        </div>
      </div>

      <ConnectionStatusBanner heartbeat={heartbeat} />

      {/* Stats row with MetricCards (3 cards) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <MetricCard
          variant="production"
          title={t('pretreat.scannedToday')}
          value={String(stats?.scannedToday ?? 0)}
          icon={Package}
        />
        <MetricCard
          variant="info"
          title={t('pretreat.sessionCount')}
          value={String(sessionCount)}
          subtitle={t('pretreat.sinceLogin')}
          icon={Clock}
        />
        <MetricCard
          variant="info"
          title={t('pretreat.pendingPretreat')}
          value={String(stats?.pendingPretreat ?? 0)}
          icon={Droplet}
        />
      </div>

      {/* Scanner Card (MATCH CatcherPage lines 159-170) */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{t('scanGarment')}</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <QRScannerInput
            onScan={handleScan}
            disabled={isScanning}
            isLoading={isScanning}
          />
        </CardContent>
      </Card>

      {/* Feedback display (conditionally shown) */}
      {result && (
        <PretreatFeedback
          result={result}
          onDismiss={handleDismiss}
        />
      )}

      {/* Scan error modal */}
      <ScanErrorModal {...feedback.modalProps} />

      {/* Superseded-sticker dialog (amber; one-tap reprint of the live replacement) */}
      <SupersededStickerDialog
        {...feedback.supersededProps}
        locationId={selectedLocation?.id ?? null}
        currentStage="Printing"
      />
    </div>
  );
}
